import type { Tab } from 'app/components/tabs';
import { Tabs } from 'app/components/tabs';
import { DASHBOARD_LAYOUT_CHILDREN_MAX_WIDTH } from 'app/components/web/dashboard-layout-constants';
import { Button } from 'app/design/primitives/button';

import { useDeviceHeight, useDeviceWidth } from 'app/hooks/use-device-size';
import { Event, track } from 'app/lib/analytics';
import { useUISize } from 'app/utils/web-helpers';
import React, { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  type FlatList,
  Platform,
  View,
} from 'react-native';
import Animated from 'react-native-reanimated';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

const AnimatedFlatList = Animated.FlatList;

interface FlatListWithTabsProps<T extends string> {
  screenName: string;
  i18nNS?: string;
  tabs: Record<T, React.JSX.Element>;
  tabsDefinition?: Tab[];
  tabsVariant?: 'default' | 'pill' | 'toggle';
  initialTab: T;
  overflow?: number;
  marginBelowTabs?: number;
  viewAboveTabs?: React.JSX.Element;
  viewBelowTabs?: React.JSX.Element;
  swipeDisabled?: boolean;
  hideTabs?: boolean;
  fixedContainerWidth?: number;
  visibleTabCount?: number;
  onTabChanged?: (tab: T) => void;
  showNavigationButtons?: boolean;
  flexListAlign?: 'flex-start' | 'center' | 'flex-end';
}

function getTabLayout({
  containerWidth,
  overflow,
  tabCount,
  visibleTabCount,
}: {
  containerWidth: number;
  overflow?: number;
  tabCount: number;
  visibleTabCount: number;
}) {
  const itemMargin = overflow ? theme.spacing['spacing.general.x4'] : 0;
  const width = containerWidth - 2 * itemMargin;
  const itemWidth = width / (visibleTabCount + (overflow ?? 0));
  const startScroll = -itemMargin;

  return {
    itemWidth,
    snapToOffsets: Array.from({ length: tabCount }, (_, index) => startScroll + index * itemWidth),
    width,
  };
}

function addActivatedTab(setActivatedTabs: React.Dispatch<React.SetStateAction<Set<string>>>, tab: string) {
  setActivatedTabs((previousTabs) => {
    if (previousTabs.has(tab)) return previousTabs;
    const nextTabs = new Set(previousTabs);
    nextTabs.add(tab);
    return nextTabs;
  });
}

function reportTabChange<T extends string>({
  newTab,
  onTabChanged,
  screenName,
  selectedTabForAnalyticsRef,
}: {
  newTab: T;
  onTabChanged?: (tab: T) => void;
  screenName: string;
  selectedTabForAnalyticsRef: React.RefObject<T>;
}) {
  if (newTab === selectedTabForAnalyticsRef.current) return;
  onTabChanged?.(newTab);
  selectedTabForAnalyticsRef.current = newTab;
  track(Event.ChangeTab, { page_name: screenName, tab: newTab });
}

function selectTab<T extends string>({
  flatListRef,
  isLargeUI,
  newTab,
  reportChange,
  selectedTab,
  setActivatedTabs,
  setSelectedTab,
  shouldUsePager,
  tabKeys,
  visibleTabCount,
}: {
  flatListRef: React.RefObject<FlatList | null>;
  isLargeUI: boolean;
  newTab: T;
  reportChange: (tab: T) => void;
  selectedTab: T;
  setActivatedTabs: React.Dispatch<React.SetStateAction<Set<string>>>;
  setSelectedTab: React.Dispatch<React.SetStateAction<T>>;
  shouldUsePager: boolean;
  tabKeys: T[];
  visibleTabCount: number;
}) {
  if (newTab === selectedTab) return;
  addActivatedTab(setActivatedTabs, newTab);

  if (!shouldUsePager) {
    setSelectedTab(newTab);
    reportChange(newTab);
    return;
  }

  // Multiple categories can share the same scroll position, especially at the end.
  // Update the selected icon even when navigating doesn't move the carousel.
  if (visibleTabCount > 1) setSelectedTab(newTab);
  flatListRef.current?.scrollToIndex({
    index: Math.min(tabKeys.indexOf(newTab), Math.max(0, tabKeys.length - visibleTabCount)),
    animated: true,
  });
  if (isLargeUI) reportChange(newTab);
}

function navigateToAdjacentTab<T extends string>(
  direction: -1 | 1,
  selectedTab: T,
  tabKeys: T[],
  onPressTab: (tab: T) => void,
) {
  const nextTab = tabKeys[tabKeys.indexOf(selectedTab) + direction];
  if (nextTab) onPressTab(nextTab);
}

function TabsHeader({
  hideTabs,
  marginBelowTabs,
  onPressTab,
  resolvedTabsDefinition,
  selectedTab,
  tabsVariant,
  viewAboveTabs,
  viewBelowTabs,
}: {
  hideTabs?: boolean;
  marginBelowTabs?: number;
  onPressTab: (tab?: string) => void;
  resolvedTabsDefinition: Tab[];
  selectedTab: string;
  tabsVariant: 'default' | 'pill' | 'toggle';
  viewAboveTabs?: React.JSX.Element;
  viewBelowTabs?: React.JSX.Element;
}) {
  if (hideTabs) return null;

  return (
    <>
      {viewAboveTabs}
      <View style={{ marginHorizontal: marginBelowTabs }}>
        <Tabs onPress={onPressTab} tabs={resolvedTabsDefinition} value={selectedTab} variant={tabsVariant} />
      </View>
      {viewBelowTabs}
    </>
  );
}

interface TabPagesProps<T extends string> {
  activatedTabs: Set<string>;
  activeTabContent: React.JSX.Element;
  flexListAlign: 'flex-start' | 'center' | 'flex-end';
  flatListRef: React.RefObject<FlatList | null>;
  handleScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  handleScrollEnd: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  initialIndex: number;
  isLargeUI: boolean;
  itemWidth: number;
  listMaxHeight: number;
  overflow?: number;
  scrollFailed: boolean;
  setScrollFailed: React.Dispatch<React.SetStateAction<boolean>>;
  shouldUsePager: boolean;
  snapToOffsets: number[];
  tabKeys: T[];
  tabs: Record<T, React.JSX.Element>;
  width: number;
  visibleTabCount: number;
}

function TabPages<T extends string>({
  activatedTabs,
  activeTabContent,
  flexListAlign,
  flatListRef,
  handleScroll,
  handleScrollEnd,
  initialIndex,
  isLargeUI,
  itemWidth,
  listMaxHeight,
  overflow,
  scrollFailed,
  setScrollFailed,
  shouldUsePager,
  snapToOffsets,
  tabKeys,
  tabs,
  width,
  visibleTabCount,
}: TabPagesProps<T>) {
  if (!shouldUsePager) {
    return (
      <View
        style={[
          { flex: 1, maxHeight: listMaxHeight },
          { width: Platform.OS === 'web' && !isLargeUI ? '100%' : width },
          isLargeUI && { alignItems: flexListAlign },
        ]}
      >
        {activeTabContent}
      </View>
    );
  }

  return (
    <AnimatedFlatList
      bounces
      data={tabKeys}
      decelerationRate={Platform.OS === 'ios' ? 'fast' : 'normal'}
      disableIntervalMomentum
      getItemLayout={(data, index) => ({ length: itemWidth, offset: itemWidth * index, index })}
      horizontal
      initialScrollIndex={initialIndex}
      keyExtractor={(item) => item}
      onScroll={handleScroll}
      scrollEventThrottle={handleScroll ? 16 : undefined}
      onMomentumScrollEnd={handleScrollEnd}
      onScrollToIndexFailed={() => {
        if (scrollFailed) return;
        setScrollFailed(true);
        flatListRef.current?.scrollToIndex({ index: initialIndex, animated: true });
      }}
      pagingEnabled={(!overflow && visibleTabCount === 1) || (Platform.OS === 'web' && visibleTabCount > 1)}
      ref={flatListRef}
      renderItem={({ index }) => (
        <View
          style={[
            { flex: 1, maxHeight: listMaxHeight },
            { width: itemWidth },
            isLargeUI && { alignItems: flexListAlign },
          ]}
        >
          {visibleTabCount > 1 || activatedTabs.has(tabKeys[index]!) ? tabs[tabKeys[index]!] : null}
        </View>
      )}
      showsHorizontalScrollIndicator={false}
      {...(overflow || visibleTabCount > 1 ? { snapToOffsets } : { snapToAlignment: 'center' })}
      style={{ flex: 1, width }}
    />
  );
}

function TabNavigationButtons({
  canNavigateNext,
  canNavigatePrevious,
  navigateToNext,
  navigateToPrevious,
  showNavigationButtons,
}: {
  canNavigateNext: boolean;
  canNavigatePrevious: boolean;
  navigateToNext: () => void;
  navigateToPrevious: () => void;
  showNavigationButtons: boolean;
}) {
  if (!showNavigationButtons) return null;

  const commonStyle = {
    position: 'absolute' as const,
    top: '50%' as const,
    zIndex: 1,
    backgroundColor: theme.colors['text.primary'],
    borderRadius: 20,
    width: 40,
    height: 40,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  };

  return (
    <>
      <Button
        width={40}
        height={40}
        variant="primary"
        justifyContent="center"
        alignItems="center"
        style={{ ...commonStyle, left: 16, opacity: canNavigatePrevious ? 1 : 0.3 }}
        onPress={navigateToPrevious}
        disabled={!canNavigatePrevious}
      >
        <IconChevronLeft strokeWidth={ICON_STROKE_WIDTH} size={24} color="white" />
      </Button>

      <Button
        width={40}
        height={40}
        variant="primary"
        justifyContent="center"
        alignItems="center"
        style={{ ...commonStyle, right: 16, opacity: canNavigateNext ? 1 : 0.3 }}
        onPress={navigateToNext}
        disabled={!canNavigateNext}
      >
        <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} size={24} color="white" />
      </Button>
    </>
  );
}

function FlatListWithTabs<T extends string>({
  screenName,
  i18nNS,
  tabs,
  tabsDefinition,
  tabsVariant = 'default',
  initialTab,
  overflow,
  marginBelowTabs,
  viewAboveTabs,
  viewBelowTabs,
  swipeDisabled,
  hideTabs,
  fixedContainerWidth,
  visibleTabCount: requestedVisibleTabCount = 1,
  onTabChanged,
  showNavigationButtons = false,
  flexListAlign = 'center',
}: FlatListWithTabsProps<T>) {
  const nsKeys = (i18nNS ?? screenName).split('.');
  const ns = nsKeys.shift();
  const { t } = useTranslation(ns, { keyPrefix: nsKeys.join('.') });

  const tabKeys = Object.keys(tabs) as T[];
  const initialIndex = Math.max(0, tabKeys.indexOf(initialTab));
  const resolvedInitialTab = tabKeys[initialIndex]!;
  const [selectedTab, setSelectedTab] = useState<T>(resolvedInitialTab);
  const flatListRef = useRef<FlatList>(null);
  const [activatedTabs, setActivatedTabs] = useState<Set<string>>(() => new Set([resolvedInitialTab]));
  const shouldUsePager = !swipeDisabled;

  const deviceWidth = useDeviceWidth();
  const { isLargeUI } = useUISize();

  const widthDesktop = fixedContainerWidth ?? DASHBOARD_LAYOUT_CHILDREN_MAX_WIDTH;
  const containerWidth = isLargeUI ? widthDesktop : deviceWidth;
  const visibleTabCount = isLargeUI ? Math.max(1, Math.min(Math.floor(requestedVisibleTabCount), tabKeys.length)) : 1;
  const isDesktopCarousel = isLargeUI && visibleTabCount > 1;
  const [desktopScroll, setDesktopScroll] = useState<{ offset: number; itemWidth: number }>();
  const { itemWidth, snapToOffsets, width } = getTabLayout({
    containerWidth,
    overflow,
    tabCount: tabKeys.length,
    visibleTabCount,
  });
  const initialScrollIndex = Math.min(initialIndex, Math.max(0, tabKeys.length - visibleTabCount));

  const selectedTabForAnalyticsRef = useRef(selectedTab);

  const trackTab = (newTab: T = selectedTab) =>
    reportTabChange({ newTab, onTabChanged, screenName, selectedTabForAnalyticsRef });

  const onPressTab = (newTab: string = selectedTab) => {
    if (isDesktopCarousel) setDesktopScroll(undefined);
    selectTab({
      flatListRef,
      isLargeUI,
      newTab: newTab as T,
      reportChange: trackTab,
      selectedTab,
      setActivatedTabs,
      setSelectedTab,
      shouldUsePager,
      tabKeys,
      visibleTabCount,
    });
  };

  // Navigation functions
  const navigateToPrevious = () => navigateToAdjacentTab(-1, selectedTab, tabKeys, onPressTab);
  const navigateToNext = () => navigateToAdjacentTab(1, selectedTab, tabKeys, onPressTab);

  const canNavigatePrevious = tabKeys.indexOf(selectedTab) > 0;
  const canNavigateNext = tabKeys.indexOf(selectedTab) < tabKeys.length - 1;

  // When initialTab changes after mount (e.g. deep link), scroll to it.
  // Track the prop explicitly so other reactive inputs can stay exhaustive without
  // changing the event that triggers the scroll.
  const previousInitialTab = useRef(initialTab);
  const applyInitialTabChange = useEffectEvent((nextInitialTab: T) => {
    const resolvedTab = tabKeys.includes(nextInitialTab) ? nextInitialTab : tabKeys[0];
    onPressTab(resolvedTab);
  });
  useEffect(() => {
    if (Object.is(previousInitialTab.current, initialTab)) return;
    previousInitialTab.current = initialTab;
    applyInitialTabChange(initialTab);
  }, [initialTab]);

  const selectTabAtOffset = (offset: number) => {
    const rawIndex = Math.abs(Math.round(offset / itemWidth));
    const maxIndex = tabKeys.length > 0 ? tabKeys.length - 1 : 0;
    const maxPage = Math.max(0, tabKeys.length - visibleTabCount);
    const index = Math.min(Math.max(rawIndex, 0), visibleTabCount > 1 ? maxPage : maxIndex);
    const selectedIndex = tabKeys.indexOf(selectedTab);
    if (visibleTabCount > 1 && selectedIndex >= index && selectedIndex < index + visibleTabCount) return;
    const newTab = tabKeys[index];
    if (newTab === undefined) return;
    setSelectedTab(newTab);
    trackTab(newTab);
    addActivatedTab(setActivatedTabs, newTab);
  };

  const handleScrollEnd = (e: NativeSyntheticEvent<NativeScrollEvent>) =>
    selectTabAtOffset(e.nativeEvent.contentOffset.x);

  // React Native Web emits onScroll, but not onMomentumScrollEnd. Wait until
  // scrolling settles so an animated tab click keeps its intended selection.
  const applyDesktopScrollEnd = useEffectEvent((offset: number) => selectTabAtOffset(offset));
  useEffect(() => {
    if (!isDesktopCarousel || desktopScroll === undefined || desktopScroll.itemWidth !== itemWidth) return;
    const timer = setTimeout(() => applyDesktopScrollEnd(desktopScroll.offset), 150);
    return () => clearTimeout(timer);
  }, [desktopScroll, isDesktopCarousel, itemWidth]);

  const restoreScrollPosition = useEffectEvent(() => {
    flatListRef.current?.scrollToIndex({
      index: Math.min(tabKeys.indexOf(selectedTab), Math.max(0, tabKeys.length - visibleTabCount)),
      animated: false,
    });
  });
  useEffect(() => {
    restoreScrollPosition();
  }, [containerWidth, visibleTabCount]);

  const resolvedTabsDefinition =
    tabsDefinition ??
    tabKeys.map((tab) => ({
      label: t(`tabs.${tab}`),
      value: tab,
    }));

  const [scrollFailed, setScrollFailed] = useState(false);

  const scrollViewContainerRef = useRef<View>(null);
  const [scrollViewContainerHeight, setScrollViewContainerHeight] = useState<number | undefined>();
  const windowHeight = useDeviceHeight();

  const fallbackScrollBodyHeight = Math.max(200, Math.round(windowHeight * 0.72));
  const listMaxHeight = scrollViewContainerHeight ?? fallbackScrollBodyHeight;

  const onScrollViewContainerLayout = (e: LayoutChangeEvent) => {
    const h = e.nativeEvent.layout.height;
    if (h > 0) {
      setScrollViewContainerHeight((prev) => (prev === h ? prev : h));
    }
  };

  const activeTabContent = tabs[selectedTab];

  return (
    <>
      <TabsHeader
        hideTabs={hideTabs}
        marginBelowTabs={marginBelowTabs}
        onPressTab={onPressTab}
        resolvedTabsDefinition={resolvedTabsDefinition}
        selectedTab={selectedTab}
        tabsVariant={tabsVariant}
        viewAboveTabs={viewAboveTabs}
        viewBelowTabs={viewBelowTabs}
      />

      <View
        style={{ flex: 1, minHeight: 0, position: 'relative' }}
        ref={scrollViewContainerRef}
        onLayout={onScrollViewContainerLayout}
      >
        <TabPages
          activatedTabs={activatedTabs}
          activeTabContent={activeTabContent}
          flexListAlign={flexListAlign}
          flatListRef={flatListRef}
          handleScroll={
            isDesktopCarousel
              ? (event) => setDesktopScroll({ offset: event.nativeEvent.contentOffset.x, itemWidth })
              : undefined
          }
          handleScrollEnd={handleScrollEnd}
          initialIndex={initialScrollIndex}
          isLargeUI={isLargeUI}
          itemWidth={itemWidth}
          listMaxHeight={listMaxHeight}
          overflow={overflow}
          scrollFailed={scrollFailed}
          setScrollFailed={setScrollFailed}
          shouldUsePager={shouldUsePager}
          snapToOffsets={snapToOffsets}
          tabKeys={tabKeys}
          tabs={tabs}
          width={width}
          visibleTabCount={visibleTabCount}
        />

        <TabNavigationButtons
          canNavigateNext={canNavigateNext}
          canNavigatePrevious={canNavigatePrevious}
          navigateToNext={navigateToNext}
          navigateToPrevious={navigateToPrevious}
          showNavigationButtons={showNavigationButtons}
        />
      </View>
    </>
  );
}

export { FlatListWithTabs };
